import { type DurationString, getDurationStringSeconds } from '@chronosphereio/core';
import { useEffect, useState } from 'react';
import { type QueryParamConfig, useQueryParam } from 'use-query-params';

export type RefreshInterval = DurationString | undefined;

export interface AutoRefreshProps {
  initialRefreshInterval?: RefreshInterval;
  refresh: () => void;
  enabledURLParams?: boolean;
}

/**
 * Implements auto-refresh functionality for the `TimeRangeProvider`, managing state and calling refresh as needed.
 */
export function useAutoRefresh({ initialRefreshInterval, refresh, enabledURLParams = false }: AutoRefreshProps) {
  const { refreshInterval, setRefreshInterval } = useSetRefreshIntervalParams({
    initialRefreshInterval,
    enabledURLParams,
  });

  const refreshIntervalInSeconds = refreshInterval ? getDurationStringSeconds(refreshInterval) : undefined;
  const refreshIntervalInMs = refreshIntervalInSeconds ? refreshIntervalInSeconds * 1000 : undefined;

  useEffect(() => {
    if (!refreshIntervalInMs) return;
    const intervalIdx = setInterval(() => {
      refresh();
    }, refreshIntervalInMs);
    return () => {
      clearInterval(intervalIdx);
    };
  }, [refreshIntervalInMs, refresh]);

  return {
    refreshInterval,
    setRefreshInterval,
    refreshIntervalInMs,
  };
}

/**
 * Returns refresh interval getter and setter, set enabledURLParams to false to disable query string serialization
 */
function useSetRefreshIntervalParams(args: { initialRefreshInterval?: RefreshInterval; enabledURLParams: boolean }) {
  const { initialRefreshInterval, enabledURLParams } = args;
  const [refreshIntervalQuery, setRefreshIntervalQuery] = useQueryParam('refresh', DurationStringParam);
  const [refreshIntervalState, setRefreshIntervalState] = useState<RefreshInterval>(initialRefreshInterval);

  // Reset to min interval if refresh query param too low and will cause performance issues
  if (refreshIntervalQuery !== undefined && !isValidRefreshInterval(refreshIntervalQuery)) {
    setRefreshIntervalQuery(`${MIN_REFRESH_INTERVAL_SECONDS}s`);
  }

  if (!enabledURLParams) {
    return {
      refreshInterval: refreshIntervalState,
      setRefreshInterval: setRefreshIntervalState,
    };
  }

  return {
    refreshInterval: refreshIntervalQuery,
    setRefreshInterval: setRefreshIntervalQuery,
  };
}

/**
 * Check whether refresh interval is below the minimum threshold. (Exported only for tests.)
 */
export function isValidRefreshInterval(refreshInterval: DurationString): boolean {
  try {
    const refreshIntervalSeconds = getDurationStringSeconds(refreshInterval);
    if (refreshIntervalSeconds === undefined) return false;
    return refreshIntervalSeconds >= MIN_REFRESH_INTERVAL_SECONDS;
  } catch {
    return false;
  }
}

const DurationStringParam: QueryParamConfig<RefreshInterval> = {
  encode: (durationString) => durationString,
  decode: (arrayStr) => {
    if (typeof arrayStr === 'string') {
      return arrayStr as DurationString;
    }
    return undefined;
  },
};

const MIN_REFRESH_INTERVAL_SECONDS = 30;
